"use client";

import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { FiTrash2 } from "react-icons/fi";
import { useAuthFetch } from "../hooks/useAuthFetch";
import { ConfirmDialog } from "./common/ConfirmDialog";
import { useFullscreenSheetModal } from "../hooks/useFullscreenSheetModal";
import { DailyLogBackButton, DailyLogBreadcrumbs, DailyLogProjectFields } from "./DailyLogFlowNav";
import ProductionTrackingUpdateMultipleModal from "./ProductionTrackingUpdateMultipleModal";

/* ================= ICON ================= */

const CloseIcon = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label="Close"
    className="flex items-center justify-center w-10 h-10 sm:w-9 sm:h-9 rounded-md hover:bg-gray-100 touch-manipulation"
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      className="shrink-0"
    >
      <path
        d="M17.7072 0.292849C17.3167 -0.0976164 16.6836 -0.0976164 16.2932 0.292849L8.99999 7.58604L1.70683 0.292849C1.31637 -0.0976164 0.683306 -0.0976164 0.29285 0.292849C-0.0976166 0.683305 -0.0976166 1.31637 0.29285 1.70683L7.58602 9.00001L0.29287 16.2931C-0.0975966 16.6836 -0.0975966 17.3166 0.29287 17.7072C0.683326 18.0976 1.31639 18.0976 1.70685 17.7072L8.99999 10.414L16.2932 17.7072C16.6836 18.0976 17.3167 18.0976 17.7072 17.7072C18.0976 17.3166 18.0976 16.6836 17.7072 16.2932L10.414 9.00001L17.7072 1.70683C18.0976 1.31637 18.0976 0.683305 17.7072 0.292849Z"
        fill="#424143"
      />
    </svg>
  </button>
);

const getProductionValidationBannerContent = (block) => {
  if (block.messageVariant === "dailyNotesRequired") {
    return {
      title: "Cannot save — Daily Notes required for the date",
      subtitle: "Enter Daily Notes in the table below before saving.",
      chips: block.dates ?? [],
    };
  }
  return {
    title: block.title || "Cannot save",
    subtitle: block.subtitle || null,
    chips: block.chips ?? [],
  };
};

const SaveValidationBanners = ({ validationBlocks, onDismiss }) => {
  if (!validationBlocks?.length) return null;

  return (
    <div className="flex shrink-0 flex-col gap-2 border-b border-gray-200 bg-white px-6 py-3">
      {validationBlocks.map((block, idx) => {
        const { title, subtitle, chips } = getProductionValidationBannerContent(block);
        return (
          <div
            key={`${block.messageVariant}-${idx}`}
            role="alert"
            className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              className="mt-0.5 h-5 w-5 shrink-0 text-amber-500"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003ZM12 8.25a.75.75 0 0 1 .75.75v3.75a.75.75 0 0 1-1.5 0V9a.75.75 0 0 1 .75-.75Zm0 8.25a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Z"
                clipRule="evenodd"
              />
            </svg>
            <div className="min-w-0 flex-1">
              <p className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-sm font-semibold text-amber-900">
                <span>{title}</span>
                {chips.map((chip) => (
                  <span
                    key={chip}
                    className="inline-flex cursor-default items-center rounded-full border border-amber-200 bg-white px-2.5 py-0.5 text-xs font-medium text-amber-900"
                  >
                    {chip}
                  </span>
                ))}
              </p>
              {subtitle && (
                <p className="mt-0.5 text-sm text-amber-800/90">{subtitle}</p>
              )}
            </div>
            <button
              type="button"
              onClick={() => onDismiss(idx)}
              className="shrink-0 text-sm text-amber-700 underline hover:text-amber-900"
            >
              Dismiss
            </button>
          </div>
        );
      })}
    </div>
  );
};

/* ================= HEADER ================= */

const Header = ({ onClose, onFlowStep, navDisabled }) => (
  <div className="relative z-10 flex shrink-0 items-center justify-between gap-3 sm:gap-4 bg-white py-3.5 sm:py-[18px] px-4 sm:px-6 rounded-t-xl border-b border-gray-200 shadow-[0_2px_5px_rgba(0,0,0,0.10)]">
    {onFlowStep ? (
      <DailyLogBreadcrumbs
        currentStep="production"
        onStepChange={onFlowStep}
        disabled={navDisabled}
      />
    ) : (
      <h2 className="font-poppins text-[18px] sm:text-[20px] font-bold text-[#2C2C2C]">
        Production Tracking
      </h2>
    )}
    <CloseIcon onClick={onClose} />
  </div>
);

/* ================= TABLE ROW ================= */

const tableGridColumns = "grid-cols-[56px_134px_292px_196px_196px_196px_196px_220px] md:grid-cols-[56px_134px_292px_196px_196px_196px_196px_minmax(196px,1fr)]";
const tableGridColumnsSelecting = "grid-cols-[40px_56px_134px_292px_196px_196px_196px_196px_220px] md:grid-cols-[40px_56px_134px_292px_196px_196px_196px_196px_minmax(196px,1fr)]";

const ProductionTableRow = ({
  date,
  items,
  isEditable,
  onChangeInstalled,
  onBlurInstalled,
  onChangeNotes,
  onDelete,
  deleteDisabled,
  isSelectDeleteMode,
  isSelected,
  onToggleSelected,
}) => {
  const singleItem = items.length === 1;
  const rowItemHeight = singleItem ? 92 : 60;
  const notesHeight = singleItem ? 72 : 44;
  const isNewlyInstalledEditable = (colKey) => colKey === "new" && isEditable;

  return (
    <div
      className={`grid ${isSelectDeleteMode ? tableGridColumnsSelecting : tableGridColumns}
  border-b
  ${isSelected ? "bg-red-50" : "bg-[#F9FAFB]"}`}
    >
      {isSelectDeleteMode && (
        <div className="flex items-center justify-center border-r" style={{ minHeight: rowItemHeight }}>
          <input
            type="checkbox"
            checked={isSelected}
            onChange={(e) => onToggleSelected?.(e.target.checked)}
            disabled={deleteDisabled}
            aria-label={`Select production log for ${date}`}
            className="h-4 w-4 rounded border-gray-300 text-[#AD2226] focus:ring-[#AD2226] disabled:opacity-50"
          />
        </div>
      )}
      <div className="flex items-center justify-center border-r" style={{ minHeight: rowItemHeight }}>
        <button
          type="button"
          onClick={onDelete}
          disabled={deleteDisabled}
          className="inline-flex h-8 w-8 items-center justify-center rounded text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          title="Delete date"
          aria-label={`Delete production log for ${date}`}
        >
          <FiTrash2 className="h-4 w-4" />
        </button>
      </div>

      {/* DATE (rowspan effect) */}
      <div className="flex items-center justify-center text-sm border-r" style={{ minHeight: rowItemHeight }}>
        {date}
      </div>

      {/* PRODUCTION QUANTITY */}
      <div className="border-r">
        {items.map((i, idx) => (
          <div
            key={idx}
            className="flex items-center justify-center text-sm text-center"
            style={{ height: rowItemHeight }}
          >
            {i.name}
          </div>
        ))}
      </div>

      {/* NUMERIC INPUT COLUMNS */}
      {["new", "toDate", "total", "percent"].map((k) => (
        <div key={k} className="border-r">
          {items.map((i, idx) => (
            <div key={idx} className="flex items-center justify-center" style={{ height: rowItemHeight }}>
              <input
                type="text"
                inputMode={k === "new" ? "decimal" : undefined}
                value={i[k]}
                onChange={isNewlyInstalledEditable(k) ? (e) => onChangeInstalled?.(i.itemKey, e.target.value) : undefined}
                onFocus={isNewlyInstalledEditable(k) ? (e) => e.target.select() : undefined}
                onBlur={isNewlyInstalledEditable(k) ? () => onBlurInstalled?.(i.itemKey) : undefined}
                readOnly={!isNewlyInstalledEditable(k)}
                className="
                  w-[150px]
                  h-[36px]
                  border
                  border-gray-300
                  rounded
                  text-center
                  text-sm
                  font-semibold
                  focus:outline-none
                  focus:ring-1
                  focus:ring-blue-500
                "
                style={{
                  backgroundColor: isNewlyInstalledEditable(k) ? "#FFFFFF" : "#F3F4F6",
                  color: "#111827",
                }}
              />
            </div>
          ))}
        </div>
      ))}

      {/* DAILY NOTES (spans rows visually) */}
      <div className="border-r last:border-r-0">
        {items.map((i, idx) => (
          <div key={idx} className="w-full px-4 pr-6 flex items-center" style={{ height: rowItemHeight }}>
            <textarea
              placeholder="Daily Notes"
              value={i.dailyNotes ?? ""}
              onChange={isEditable ? (e) => onChangeNotes?.(i.itemKey, e.target.value) : undefined}
              readOnly={!isEditable}
              className="
          w-full
          border
          border-gray-300
          rounded
          px-3
          py-1.5
          resize-none
          text-sm
          leading-[1.3]
        "
              style={{
                height: notesHeight,
                backgroundColor: isEditable ? "#FFFFFF" : "#F3F4F6",
                color: "#111827",
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );
};


/* ================= TABLE ================= */

const ProductionTable = ({
  loading,
  error,
  tableRows,
  onChangeInstalled,
  onBlurInstalled,
  onChangeNotes,
  onDeleteRow,
  deleteDisabled = false,
  compactTopSpacing = false,
  isSelectDeleteMode = false,
  selectedWorkDates,
  onToggleRowSelected,
  onToggleAllRowsSelected,
  hideEmptyState = false,
}) => {
  const selectAllCheckboxRef = useRef(null);
  const selectedSet = selectedWorkDates instanceof Set ? selectedWorkDates : new Set(selectedWorkDates || []);
  const selectedCount = tableRows.reduce(
    (count, row) => count + (selectedSet.has(row.workDateIso) ? 1 : 0),
    0
  );
  const allRowsSelected = tableRows.length > 0 && selectedCount === tableRows.length;

  useEffect(() => {
    if (!selectAllCheckboxRef.current) return;
    selectAllCheckboxRef.current.indeterminate = selectedCount > 0 && !allRowsSelected;
  }, [selectedCount, allRowsSelected]);

  const tableMinWidth = isSelectDeleteMode
    ? "min-w-[1526px] md:min-w-[1502px]"
    : "min-w-[1486px] md:min-w-[1462px]";
  const gridColumns = isSelectDeleteMode ? tableGridColumnsSelecting : tableGridColumns;
  const topSpacingClass = compactTopSpacing ? "mt-2 mb-4 md:mb-6" : "mt-6 mb-4 md:mb-6";

  if (loading) {
    return (
      <section className={`w-full ${topSpacingClass} px-6`}>
        <div className="border border-gray-300 rounded-md bg-white p-8 text-center text-gray-500">
          Loading production tracking...
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className={`w-full ${topSpacingClass} px-6`}>
        <div className="border border-gray-300 rounded-md bg-white p-8 text-center text-red-600">
          {error}
        </div>
      </section>
    );
  }

  return (
    <section className={`w-full ${topSpacingClass} px-4 md:px-6 overflow-x-hidden`}>
      {/* OUTER BORDER */}
      <div
        className="border border-gray-300 rounded-md bg-white overflow-x-auto max-w-full"
      >
        <div className={tableMinWidth}>
          {/* HEADER */}
          <div
            className={`
    grid
    ${gridColumns}
    bg-gray-200
    border-b
    border-gray-300
    font-semibold
    text-sm
  `}
          >
            {isSelectDeleteMode && (
              <div className="flex items-center justify-center h-[56px] border-r">
                <input
                  ref={selectAllCheckboxRef}
                  type="checkbox"
                  checked={allRowsSelected}
                  onChange={(e) => onToggleAllRowsSelected?.(e.target.checked)}
                  disabled={deleteDisabled || tableRows.length === 0}
                  aria-label="Select all dates"
                  className="h-4 w-4 rounded border-gray-300 text-[#AD2226] focus:ring-[#AD2226] disabled:opacity-50"
                />
              </div>
            )}
            {[
              "",
              "Date",
              "Production Quantity",
              "Newly Installed",
              "Installed to Date",
              "Total Estimated",
              "% Installed",
              "Daily Notes",
            ].map((h, i) => (
              <div
                key={i}
                className="
        flex
        items-center
        justify-center
        text-center
        h-[56px]
        border-r
        last:border-r-0
      "
              >
                {h}
              </div>
            ))}
          </div>

          {/* ROWS */}
          {tableRows.map((row, rowIdx) => (
            <ProductionTableRow
              key={`${row.workDateIso}-${rowIdx}`}
              date={row.date}
              items={row.items}
              isEditable={row.isEditable}
              onChangeInstalled={onChangeInstalled}
              onBlurInstalled={onBlurInstalled}
              onChangeNotes={onChangeNotes}
              onDelete={() => onDeleteRow?.(row)}
              deleteDisabled={deleteDisabled}
              isSelectDeleteMode={isSelectDeleteMode}
              isSelected={selectedSet.has(row.workDateIso)}
              onToggleSelected={(checked) => onToggleRowSelected?.(row.workDateIso, checked)}
            />
          ))}
        </div>
        {tableRows.length === 0 && !hideEmptyState && (
          <div className="px-6 py-8 text-center text-sm text-gray-500">
            No production tracking data found.
          </div>
        )}
      </div>
    </section>
  );
};


/* ================= FOOTER ================= */

const SubmitButton = ({ onSubmit, disabled, saving }) => (
    <button
      type="button"
      onClick={onSubmit}
      disabled={disabled}
      className={`h-12 sm:h-11 min-w-0 flex-1 sm:flex-none sm:w-auto sm:max-w-none sm:px-16 rounded-md text-white text-sm font-semibold touch-manipulation ${
        disabled ? "bg-gray-400 cursor-not-allowed" : "bg-[#AD2226] hover:bg-[#8F1B1E]"
      }`}
    >
      {saving ? "Saving..." : "Save and Log Inventory"}
    </button>
);

/* ================= CONTENT ================= */

const getDefaultWorkDate = () => new Date().toISOString().split("T")[0];
const formatIsoDateToTable = (isoDate) => {
  if (!isoDate || typeof isoDate !== "string") return "--";
  const parts = isoDate.split("-");
  if (parts.length !== 3) return isoDate;
  const [yyyy, mm, dd] = parts;
  return `${mm}/${dd}/${yyyy.slice(-2)}`;
};

const formatIsoDateToMdy = (isoDate) => {
  if (!isoDate || typeof isoDate !== "string") return "--";
  const parts = isoDate.split("-");
  if (parts.length !== 3) return isoDate;
  const [yyyy, mm, dd] = parts;
  return `${mm}-${dd}-${yyyy}`;
};

const formatNumberValue = (value) => {
  if (value == null || value === "" || value === "--") return "--";
  const parsed = parseFloat(value);
  if (Number.isNaN(parsed)) return String(value);
  return Math.round(parsed).toLocaleString();
};

const parseNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const PARTIAL_NUMBER_INPUT_RE = /^\d*\.?\d*$/;

const isValidPartialNumberInput = (value) => value === '' || PARTIAL_NUMBER_INPUT_RE.test(value);

const normalizeNumberOnBlur = (value) => {
  if (value === '' || value == null) return 0;
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
};

const buildItemKey = (row, idx) => {
  const code = String(row?.code || "").trim();
  const name = String(row?.description || row?.activity || "").trim();
  if (code || name) return `${code}|${name}`;
  return `item-${idx}`;
};

/** Cumulative through prior days only (so UI can add today's draft "new" for live totals). */
const buildItemsFromLogContext = (prodRows, entryMap, installedToDateByItem) =>
  prodRows.map((row, idx) => {
    const totalEstimatedRaw =
      row.totalEstimated != null && row.totalEstimated !== "" && row.totalEstimated !== "--"
        ? row.totalEstimated
        : 0;
    const totalEstimated = parseNumber(totalEstimatedRaw);
    const itemKey = buildItemKey(row, idx);
    const saved = entryMap.get(itemKey);
    const newlyInstalled = saved ? parseNumber(saved.newlyInstalled) : 0;
    const cumulativeEndOfDay = parseNumber(installedToDateByItem[itemKey]);
    const baselineCumulative = Math.max(0, cumulativeEndOfDay - newlyInstalled);
    const displayTotal = baselineCumulative + newlyInstalled;
    const percentInstalled =
      totalEstimated > 0 ? `${Math.round((displayTotal / totalEstimated) * 100)}%` : "--";

    return {
      itemKey,
      name: row.description ?? row.activity ?? "--",
      new: String(newlyInstalled),
      toDate: formatNumberValue(displayTotal),
      total: formatNumberValue(totalEstimated),
      percent: percentInstalled,
      totalEstimated,
      dailyNotes: saved?.dailyNotes || "",
      baselineCumulative,
    };
  });

const hasUsefulProductionRows = (prodRows) => {
  const rows = Array.isArray(prodRows) ? prodRows : [];
  if (!rows.length) return false;
  return rows.some((row) => {
    const hasName = Boolean((row?.description || row?.activity || "").trim());
    const hasEstimated = row?.totalEstimated != null && row?.totalEstimated !== "" && row?.totalEstimated !== "--";
    return hasName || hasEstimated;
  });
};

/** Same labor-entry query shape as Enter Data (Labor Tracking) modal. */
const buildLaborEntryUrl = (base, projectNo, workDate, { scopeName, scopeId, projectActivities, presenceOnly } = {}) => {
  const path = `${base}/api/miter/production-performance/${encodeURIComponent(projectNo)}/labor-entry/${workDate}`;
  const params = new URLSearchParams();
  if (scopeName) params.set("scope", scopeName);
  if (scopeId) params.set("excludeScopeId", scopeId);
  if (presenceOnly) params.set("presenceOnly", "1");
  if (projectActivities?.length) {
    params.set("scopes", JSON.stringify(projectActivities));
  }
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
};

/** Drop saved dates and rebuild Installed to Date from the rows that remain. */
const recalculateSummariesWithoutDates = (dateSummaries, removedWorkDateIsos) => {
  const removed = removedWorkDateIsos instanceof Set
    ? removedWorkDateIsos
    : new Set(removedWorkDateIsos ? [removedWorkDateIsos] : []);
  const remaining = (dateSummaries || []).filter((summary) => !removed.has(summary.workDate));
  const ascending = [...remaining].sort((a, b) => a.workDate.localeCompare(b.workDate));
  const running = {};
  const cumulativeByDate = new Map();
  ascending.forEach((summary) => {
    (summary.entries || []).forEach((entry) => {
      const key = String(entry.itemKey || "").trim();
      if (!key) return;
      running[key] = (running[key] || 0) + (Number(entry.newlyInstalled) || 0);
    });
    cumulativeByDate.set(summary.workDate, { ...running });
  });
  return remaining.map((summary) => ({
    ...summary,
    installedToDateByItem: cumulativeByDate.get(summary.workDate) || {},
  }));
};

const latestCumulativeOnOrBefore = (dateSummaries, workDateIso) => {
  const prior = (dateSummaries || [])
    .filter((summary) => summary.workDate && summary.workDate <= workDateIso)
    .sort((a, b) => b.workDate.localeCompare(a.workDate))[0];
  return prior?.installedToDateByItem || {};
};

const mapSummariesToTableRows = (
  dateSummaries,
  prodRows,
  targetWorkDate,
  canEditSelectedWorkDate
) =>
  dateSummaries.map((d) => {
    const entryMap = new Map((d.entries || []).map((e) => [String(e.itemKey), e]));
    const items = buildItemsFromLogContext(prodRows, entryMap, d.installedToDateByItem || {});
    return {
      workDateIso: d.workDate,
      date: formatIsoDateToTable(d.workDate),
      items,
      isEditable: d.workDate === targetWorkDate && canEditSelectedWorkDate,
      isSaved: true,
    };
  });

/** Editable row for a date that has crew hours but no saved production log yet. */
const rowsWithEditableWorkDate = (
  dateSummaries,
  prodRows,
  targetWorkDate,
  entryMap,
  installedToDateByItem
) => {
  const rows = mapSummariesToTableRows(dateSummaries, prodRows, targetWorkDate, true)
    .filter((row) => row.workDateIso !== targetWorkDate);
  rows.push({
    workDateIso: targetWorkDate,
    date: formatIsoDateToTable(targetWorkDate),
    items: buildItemsFromLogContext(prodRows, entryMap, installedToDateByItem || {}),
    isEditable: true,
    isSaved: false,
  });
  rows.sort((a, b) => b.workDateIso.localeCompare(a.workDateIso));
  return rows;
};

/** Every saved date stays in the table. The selected date is editable only when allowed. */
const rowsForWorkDate = (
  dateSummaries,
  prodRows,
  targetWorkDate,
  canEditSelectedWorkDate,
  entryMap = new Map(),
  installedToDateByItem = null
) => {
  const summaries = dateSummaries || [];
  const savedIsoSet = new Set(summaries.map((d) => d.workDate));
  if (canEditSelectedWorkDate && targetWorkDate && !savedIsoSet.has(targetWorkDate)) {
    return rowsWithEditableWorkDate(
      summaries,
      prodRows,
      targetWorkDate,
      entryMap,
      installedToDateByItem || latestCumulativeOnOrBefore(summaries, targetWorkDate)
    );
  }
  const rows = mapSummariesToTableRows(
    summaries,
    prodRows,
    targetWorkDate,
    canEditSelectedWorkDate
  );
  rows.sort((a, b) => b.workDateIso.localeCompare(a.workDateIso));
  return rows;
};

const WORK_DATE_DEBOUNCE_MS = 400;
/** A just-saved crew timesheet can miss the first Miter read. Check again before the empty state. */
const LABOR_MISS_RETRY_MS = [800, 1600, 2400];
const RATE_LIMIT_RETRY_MS = 2000;

const isRateLimitError = (errOrStatus, message = "") => {
  const status =
    typeof errOrStatus === "number" ? errOrStatus : errOrStatus?.response?.status;
  const msg =
    message || errOrStatus?.response?.data?.message || errOrStatus?.message || "";
  return status === 429 || status === 503 || /rate limit/i.test(String(msg));
};

const sleep = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));

const axiosGetWithRateLimitRetry = async (url, config = {}, { attempts = 2 } = {}) => {
  let lastError;
  for (let i = 0; i < attempts; i += 1) {
    try {
      const res = await axios.get(url, config);
      if (isRateLimitError(res.status, res.data?.message) && i < attempts - 1) {
        await sleep(RATE_LIMIT_RETRY_MS);
        continue;
      }
      return res;
    } catch (err) {
      if (err?.code === "ERR_CANCELED") throw err;
      lastError = err;
      if (isRateLimitError(err) && i < attempts - 1) {
        await sleep(RATE_LIMIT_RETRY_MS);
        continue;
      }
      throw err;
    }
  }
  throw lastError;
};

const ProductionTrackingContent = ({ onClose, initialContext, onLogInventory, onProductionSaved, onFlowNavigate, isActive = true }) => {
  const { getAuthConfig, isReady } = useAuthFetch();
  const [projectNo, setProjectNo] = useState(initialContext?.projectNo ?? "1-25401");
  const [projectName, setProjectName] = useState(initialContext?.projectName ?? "Project Name");
  const [workDate, setWorkDate] = useState(initialContext?.workDate ?? getDefaultWorkDate());
  const [division, setDivision] = useState(initialContext?.division ?? "Roof Decks");
  const [scopeId, setScopeId] = useState(initialContext?.scopeId ?? "");
  const [projectActivities, setProjectActivities] = useState(
    initialContext?.projectActivities ?? []
  );
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, rows: [] });
  const [isSelectDeleteMode, setIsSelectDeleteMode] = useState(false);
  const [isUpdateMultipleOpen, setIsUpdateMultipleOpen] = useState(false);
  const [updateMultipleSession, setUpdateMultipleSession] = useState(null);
  const [productionReady, setProductionReady] = useState(false);
  const [selectedWorkDates, setSelectedWorkDates] = useState([]);
  const [error, setError] = useState("");
  const [saveMessage, setSaveMessage] = useState(null);
  const [tableRows, setTableRows] = useState([]);
  const [refreshNonce, setRefreshNonce] = useState(0);
  /** Miter timesheet or saved labor allocation exists for the selected work date. */
  const [hasLaborForWorkDate, setHasLaborForWorkDate] = useState(true);
  /** User may edit/save production for the selected work date (labor or existing production log). */
  const [canEditProductionForWorkDate, setCanEditProductionForWorkDate] = useState(true);
  /** True while the selected date's timesheet check is still in flight. */
  const [laborCheckPending, setLaborCheckPending] = useState(false);
  /** True from the moment Work Date changes until that date's editable row is ready. */
  const [workDateLoading, setWorkDateLoading] = useState(false);

  const tableCacheRef = useRef({
    ready: false,
    prodRows: [],
    dateSummaries: [],
    resolvedScopeId: null,
  });
  const workDateRequestSeqRef = useRef(0);
  const tableFetchSeqRef = useRef(0);
  const laborCacheRef = useRef(new Map());
  const laborAbortRef = useRef(null);
  const workDateDebounceRef = useRef(null);
  const lastSyncedWorkDateRef = useRef(null);
  /** Crew grid already has Project Labor hours for this date, even if Miter has not caught up. */
  const crewLaborHintRef = useRef({ workDate: "", hasCrewProjectLabor: false });
  const wasActiveRef = useRef(isActive);
  /** A bulk update just saved. The next tracking reload should leave that confirmation up. */
  const keepSuccessRef = useRef(false);

  const laborCacheKey = useCallback(
    (targetWorkDate) =>
      `${projectNo}|${scopeId}|${division}|${targetWorkDate}`,
    [projectNo, scopeId, division]
  );

  const applyInstantWorkDateUI = useCallback((targetWorkDate) => {
    const cache = tableCacheRef.current;
    if (!cache.ready || !cache.dateSummaries.length) return;

    setError("");
    let mappedRows = mapSummariesToTableRows(
      cache.dateSummaries,
      cache.prodRows,
      targetWorkDate,
      false
    );
    mappedRows.sort((a, b) => b.workDateIso.localeCompare(a.workDateIso));
    setTableRows(mappedRows);
  }, []);

  const fetchLaborForWorkDate = useCallback(
    async (targetWorkDate, requestSeq, signal) => {
      const cacheKey = laborCacheKey(targetWorkDate);
      if (laborCacheRef.current.has(cacheKey)) {
        return laborCacheRef.current.get(cacheKey);
      }

      const base = process.env.REACT_APP_BACKEND_BASEURL;
      const url = buildLaborEntryUrl(base, projectNo, targetWorkDate, {
        scopeName: division,
        scopeId,
        projectActivities,
        presenceOnly: true,
      });
      const laborEntryRes = await axiosGetWithRateLimitRetry(url, await getAuthConfig({ signal }));
      if (requestSeq !== workDateRequestSeqRef.current) return null;

      const payload = laborEntryRes.data?.data || {};
      const employees = payload.employees || [];
      const hasLabor = typeof payload.hasScopeLabor === "boolean"
        ? payload.hasScopeLabor
        : employees.some((employee) => Number(employee.totalHours) > 0);
      // A miss can be stale (timesheet just saved). Don't pin it for this modal session.
      if (hasLabor) laborCacheRef.current.set(cacheKey, true);
      return hasLabor;
    },
    [projectNo, division, scopeId, projectActivities, laborCacheKey, getAuthConfig]
  );

  const syncTableToWorkDate = useCallback(
    async (targetWorkDate) => {
      const cache = tableCacheRef.current;
      if (!cache.ready || !cache.resolvedScopeId || !projectNo) return;

      const requestSeq = ++workDateRequestSeqRef.current;
      const finishDateLoad = () => {
        if (requestSeq !== workDateRequestSeqRef.current) return;
        setLaborCheckPending(false);
        setWorkDateLoading(false);
      };
      const base = process.env.REACT_APP_BACKEND_BASEURL;
      const workDateValid = /^\d{4}-\d{2}-\d{2}$/.test(targetWorkDate);
      const prodRows = cache.prodRows;

      applyInstantWorkDateUI(targetWorkDate);
      setWorkDateLoading(false);
      setLaborCheckPending(true);

      if (!workDateValid) {
        setHasLaborForWorkDate(false);
        setCanEditProductionForWorkDate(false);
        finishDateLoad();
        return;
      }

      laborAbortRef.current?.abort();
      const abortController = new AbortController();
      laborAbortRef.current = abortController;

      // Saved logs are local. Reload them before the timesheet check so other
      // dates stay on screen even when the selected date has no timesheet.
      let summaries = Array.isArray(cache.dateSummaries) ? cache.dateSummaries : [];
      try {
        const params = new URLSearchParams();
        if (division) params.set("scope", division);
        const qs = params.toString();
        const listUrl = `${base}/api/miter/production-performance/${encodeURIComponent(projectNo)}/production-logs/${encodeURIComponent(cache.resolvedScopeId)}${qs ? `?${qs}` : ""}`;
        const listRes = await axiosGetWithRateLimitRetry(
          listUrl,
          await getAuthConfig({ signal: abortController.signal })
        );
        if (requestSeq !== workDateRequestSeqRef.current) return;
        if (Array.isArray(listRes.data?.data?.dates)) {
          summaries = listRes.data.data.dates;
          if (tableCacheRef.current.ready) {
            tableCacheRef.current = {
              ...tableCacheRef.current,
              dateSummaries: summaries,
            };
          }
        }
      } catch (err) {
        if (err?.code === "ERR_CANCELED") return;
        if (requestSeq !== workDateRequestSeqRef.current) return;
      }

      const savedIsoSet = new Set(summaries.map((d) => d.workDate));
      const hasExistingProductionLog = savedIsoSet.has(targetWorkDate);
      const crewHintMatches =
        crewLaborHintRef.current.hasCrewProjectLabor &&
        crewLaborHintRef.current.workDate === targetWorkDate;

      if (crewHintMatches) {
        setHasLaborForWorkDate(true);
        setCanEditProductionForWorkDate(true);
      }
      setTableRows(rowsForWorkDate(summaries, prodRows, targetWorkDate, crewHintMatches));
      // Saved rows are on screen. The timesheet check below must not keep "Loading..." up.
      setWorkDateLoading(false);

      let hasLaborForSelectedDate = false;
      try {
        const readLabor = () =>
          fetchLaborForWorkDate(targetWorkDate, requestSeq, abortController.signal);
        let laborResult = await readLabor();
        if (laborResult == null || requestSeq !== workDateRequestSeqRef.current) return;
        hasLaborForSelectedDate = laborResult;
        // A just-saved crew sheet can miss the first read. Only that case is worth another look.
        if (!hasLaborForSelectedDate && !hasExistingProductionLog && crewHintMatches) {
          for (const delayMs of LABOR_MISS_RETRY_MS) {
            await sleep(delayMs);
            if (abortController.signal.aborted || requestSeq !== workDateRequestSeqRef.current) {
              return;
            }
            laborResult = await readLabor();
            if (laborResult == null || requestSeq !== workDateRequestSeqRef.current) return;
            hasLaborForSelectedDate = laborResult;
            if (hasLaborForSelectedDate) break;
          }
        }
      } catch (err) {
        if (err?.code === "ERR_CANCELED") return;
        if (requestSeq !== workDateRequestSeqRef.current) return;
        if (isRateLimitError(err)) {
          if (!crewHintMatches) {
            setError("Miter is busy right now. Wait a moment, then change the work date to refresh.");
            setCanEditProductionForWorkDate(hasExistingProductionLog);
            setHasLaborForWorkDate(false);
          }
          setTableRows(
            rowsForWorkDate(
              summaries,
              prodRows,
              targetWorkDate,
              crewHintMatches || hasExistingProductionLog
            )
          );
          finishDateLoad();
          return;
        }
        hasLaborForSelectedDate = false;
      }

      const canEditSelectedWorkDate = hasLaborForSelectedDate || crewHintMatches;
      setHasLaborForWorkDate(canEditSelectedWorkDate);
      setCanEditProductionForWorkDate(canEditSelectedWorkDate);

      let entryMap = new Map();
      let installedToDateByItem = latestCumulativeOnOrBefore(summaries, targetWorkDate);
      if (canEditSelectedWorkDate && !savedIsoSet.has(targetWorkDate) && hasLaborForSelectedDate) {
        try {
          const singleDateParams = new URLSearchParams();
          if (division) singleDateParams.set("scope", division);
          const singleDateQs = singleDateParams.toString();
          const singleDateUrl = `${base}/api/miter/production-performance/${encodeURIComponent(projectNo)}/production-logs/${encodeURIComponent(cache.resolvedScopeId)}/${encodeURIComponent(targetWorkDate)}${singleDateQs ? `?${singleDateQs}` : ""}`;
          const singleRes = await axiosGetWithRateLimitRetry(
            singleDateUrl,
            await getAuthConfig({ signal: abortController.signal })
          );
          if (requestSeq !== workDateRequestSeqRef.current) return;
          const entries = singleRes.data?.data?.entries || [];
          entryMap = new Map(entries.map((e) => [String(e.itemKey), e]));
          installedToDateByItem = singleRes.data?.data?.installedToDateByItem || installedToDateByItem;
        } catch (err) {
          if (err?.code === "ERR_CANCELED") return;
          if (requestSeq !== workDateRequestSeqRef.current) return;
        }
      }

      if (requestSeq !== workDateRequestSeqRef.current) return;
      setTableRows(
        rowsForWorkDate(
          summaries,
          prodRows,
          targetWorkDate,
          canEditSelectedWorkDate,
          entryMap,
          installedToDateByItem
        )
      );
      finishDateLoad();
    },
    [projectNo, division, applyInstantWorkDateUI, fetchLaborForWorkDate, getAuthConfig]
  );

  useEffect(() => {
    if (!initialContext) return;
    if (initialContext.projectNo != null) setProjectNo(initialContext.projectNo);
    if (initialContext.projectName != null) setProjectName(initialContext.projectName);
    if (initialContext.workDate != null) setWorkDate(initialContext.workDate);
    if (initialContext.division != null) setDivision(initialContext.division);
    if (initialContext.scopeId != null) setScopeId(initialContext.scopeId);
    if (initialContext.projectActivities != null) {
      setProjectActivities(initialContext.projectActivities);
    }
    const hintedDate = initialContext.workDate || "";
    const hinted = Boolean(initialContext.hasCrewProjectLabor) && Boolean(hintedDate);
    const prevHint = crewLaborHintRef.current;
    if (Object.prototype.hasOwnProperty.call(initialContext, "hasCrewProjectLabor")) {
      crewLaborHintRef.current = {
        workDate: hintedDate,
        hasCrewProjectLabor: hinted,
      };
    }
    const hintBecameTrue =
      hinted &&
      (!prevHint.hasCrewProjectLabor || prevHint.workDate !== hintedDate);
    if (hintBecameTrue && tableCacheRef.current.ready) {
      syncTableToWorkDate(hintedDate);
    }
  }, [initialContext, syncTableToWorkDate]);

  useEffect(() => {
    if (!projectNo || !isReady) {
      setTableRows([]);
      setProductionReady(false);
      setError("");
      return;
    }

    const fetchProductionTracking = async () => {
      const fetchSeq = ++tableFetchSeqRef.current;
      const previousCache = tableCacheRef.current;
      laborCacheRef.current.clear();
      lastSyncedWorkDateRef.current = null;
      setLoading(true);
      setError("");

      try {
        const base = process.env.REACT_APP_BACKEND_BASEURL;
        let resolvedScopeId = scopeId || previousCache.resolvedScopeId;

        if (!resolvedScopeId && division && projectActivities?.length) {
          const target = String(division).trim().toLowerCase();
          const matched = projectActivities.find(
            (r) => String(r?.scope || "").trim().toLowerCase() === target
          );
          if (matched?.scopeId) resolvedScopeId = matched.scopeId;
        }

        if (!resolvedScopeId && division) {
          const perfRes = await axiosGetWithRateLimitRetry(
            `${base}/api/miter/production-performance/${encodeURIComponent(projectNo)}`,
            await getAuthConfig()
          );
          if (fetchSeq !== tableFetchSeqRef.current) return;
          if (perfRes.data?.status === "success" && Array.isArray(perfRes.data?.data?.rows)) {
            const target = String(division).trim().toLowerCase();
            const matched = perfRes.data.data.rows.find(
              (r) => String(r?.scope || "").trim().toLowerCase() === target
            );
            if (matched?.scopeId) resolvedScopeId = matched.scopeId;
          }
        }

        if (!resolvedScopeId) {
          tableCacheRef.current = {
            ready: false,
            prodRows: [],
            dateSummaries: [],
            resolvedScopeId: null,
          };
          setTableRows([]);
          setProductionReady(false);
          setError("Unable to resolve scope for production tracking.");
          return;
        }

        const logDataUrl = division
          ? `${base}/api/miter/production-performance/${encodeURIComponent(projectNo)}/log-production-data/${encodeURIComponent(resolvedScopeId)}?scope=${encodeURIComponent(division)}`
          : `${base}/api/miter/production-performance/${encodeURIComponent(projectNo)}/log-production-data/${encodeURIComponent(resolvedScopeId)}`;

        const res = await axiosGetWithRateLimitRetry(logDataUrl, await getAuthConfig());
        if (fetchSeq !== tableFetchSeqRef.current) return;

        if (res.data?.status !== "success" || !res.data?.data) {
          setTableRows([]);
          setProductionReady(false);
          setError(res.data?.message || "Failed to load production tracking data.");
          return;
        }

        const prodRows = res.data.data.prodRows || [];
        const dateSummaries = res.data.data.dates || [];

        if (!hasUsefulProductionRows(prodRows)) {
          tableCacheRef.current = {
            ready: false,
            prodRows: [],
            dateSummaries: [],
            resolvedScopeId: null,
          };
          setTableRows([]);
          setProductionReady(false);
          setSaveMessage(null);
          return;
        }

        tableCacheRef.current = {
          ready: true,
          prodRows,
          dateSummaries,
          resolvedScopeId,
        };

        if (resolvedScopeId && resolvedScopeId !== scopeId) {
          setScopeId(resolvedScopeId);
        }

        setProductionReady(true);
        if (keepSuccessRef.current) {
          keepSuccessRef.current = false;
        } else {
          setSaveMessage(null);
        }
      } catch (err) {
        if (fetchSeq !== tableFetchSeqRef.current) return;

        if (isRateLimitError(err) && previousCache.ready) {
          setError("Miter rate limit reached. Please wait a moment before changing dates again.");
          return;
        }

        tableCacheRef.current = {
          ready: false,
          prodRows: [],
          dateSummaries: [],
          resolvedScopeId: null,
        };
        setTableRows([]);
        setProductionReady(false);
        setHasLaborForWorkDate(false);
        setCanEditProductionForWorkDate(false);
        setError(
          isRateLimitError(err)
            ? "Miter rate limit reached. Please wait a moment and try again."
            : err.response?.data?.message || err.message || "Failed to load production tracking data."
        );
      } finally {
        if (fetchSeq === tableFetchSeqRef.current) {
          setLoading(false);
        }
      }
    };

    fetchProductionTracking();
  }, [projectNo, division, refreshNonce, projectActivities, isReady, getAuthConfig]);

  // Work date: instant UI from cache, debounced labor API (matches Enter Data modal).
  useEffect(() => {
    if (!tableCacheRef.current.ready || loading) return;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(workDate)) return;

    applyInstantWorkDateUI(workDate);
    // Show checking status immediately (covers debounce + labor API wait).
    setLaborCheckPending(true);

    if (workDateDebounceRef.current) {
      window.clearTimeout(workDateDebounceRef.current);
    }

    const isInitialSync = lastSyncedWorkDateRef.current === null;
    const runSync = () => {
      lastSyncedWorkDateRef.current = workDate;
      syncTableToWorkDate(workDate);
    };

    if (isInitialSync) {
      runSync();
      return undefined;
    }

    workDateDebounceRef.current = window.setTimeout(runSync, WORK_DATE_DEBOUNCE_MS);
    return () => {
      if (workDateDebounceRef.current) {
        window.clearTimeout(workDateDebounceRef.current);
      }
    };
  }, [workDate, loading, syncTableToWorkDate, applyInstantWorkDateUI]);

  // Coming back to this screen re-checks the date. The first visit can run
  // before the crew timesheet save has landed.
  useEffect(() => {
    const becameActive = Boolean(isActive) && !wasActiveRef.current;
    wasActiveRef.current = Boolean(isActive);
    if (!becameActive) return undefined;
    if (!tableCacheRef.current.ready || loading) return undefined;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(workDate)) return undefined;
    syncTableToWorkDate(workDate);
    return undefined;
  }, [isActive, workDate, loading, syncTableToWorkDate]);

  useEffect(
    () => () => {
      laborAbortRef.current?.abort();
      if (workDateDebounceRef.current) {
        window.clearTimeout(workDateDebounceRef.current);
      }
    },
    []
  );

  useEffect(() => {
    if (!saveMessage) return undefined;
    const delayMs = saveMessage.type === "error" ? 15000 : 5000;
    const timeoutId = window.setTimeout(() => {
      setSaveMessage(null);
    }, delayMs);
    return () => window.clearTimeout(timeoutId);
  }, [saveMessage]);

  const dismissValidationBlock = (blockIndex) => {
    setSaveMessage((prev) => {
      if (!prev?.validationBlocks?.length) return null;
      const remaining = prev.validationBlocks.filter((_, i) => i !== blockIndex);
      if (remaining.length === 0) return null;
      return { ...prev, validationBlocks: remaining };
    });
  };

  const runSaveValidations = () => {
    if (!projectNo || !scopeId || !workDate) {
      setSaveMessage({
        type: "error",
        validationBlocks: [{
          messageVariant: "generic",
          title: "Cannot save — required fields missing",
          subtitle: "Project, date, and scope are required.",
        }],
      });
      return false;
    }
    if (!canEditProductionForWorkDate) {
      setSaveMessage({
        type: "error",
        validationBlocks: [{
          messageVariant: "generic",
          title: "Cannot save — no timesheet data",
          subtitle: `No timesheet data for ${formatIsoDateToMdy(workDate)} in Miter. Change Work Date to log production.`,
        }],
      });
      return false;
    }
    const editableRow = tableRows.find((r) => r.isEditable);
    if (!editableRow) {
      setSaveMessage({
        type: "error",
        validationBlocks: [{
          messageVariant: "generic",
          title: "Cannot save — no editable row",
          subtitle: "No editable production row for the selected work date.",
        }],
      });
      return false;
    }
    const itemNeedsDailyNotes = (item) => {
      if (parseNumber(item.new) === 0) return false;
      return !String(item.dailyNotes ?? "").trim();
    };
    const rowMissingDailyNotes = (row) =>
      row.isEditable && row.items.some(itemNeedsDailyNotes);

    const rowsNeedingNotes = tableRows.filter(rowMissingDailyNotes);
    if (rowsNeedingNotes.length > 0) {
      setSaveMessage({
        type: "error",
        validationBlocks: [{
          messageVariant: "dailyNotesRequired",
          dates: rowsNeedingNotes.map((row) => formatIsoDateToMdy(row.workDateIso)),
        }],
      });
      return false;
    }
    return true;
  };

  const updateInstalledItem = (item, value) => {
    const today = value === '' ? 0 : parseNumber(value);
    const displayTotal = (item.baselineCumulative ?? 0) + today;
    const pct =
      item.totalEstimated > 0
        ? `${Math.round((displayTotal / item.totalEstimated) * 100)}%`
        : "--";
    return {
      ...item,
      new: value,
      toDate: formatNumberValue(displayTotal),
      percent: pct,
    };
  };

  const handleChangeInstalled = (itemKey, value) => {
    if (!isValidPartialNumberInput(value)) return;
    setTableRows((prev) => prev.map((row) => {
      if (!row.isEditable) return row;
      return {
        ...row,
        items: row.items.map((item) => {
          if (item.itemKey !== itemKey) return item;
          return updateInstalledItem(item, value);
        }),
      };
    }));
  };

  const handleBlurInstalled = (itemKey) => {
    setTableRows((prev) => prev.map((row) => {
      if (!row.isEditable) return row;
      return {
        ...row,
        items: row.items.map((item) => {
          if (item.itemKey !== itemKey) return item;
          const normalized = normalizeNumberOnBlur(item.new);
          const normalizedStr = String(normalized);
          if (item.new === normalizedStr || item.new === normalized) return item;
          return updateInstalledItem(item, normalizedStr);
        }),
      };
    }));
  };

  const handleChangeNotes = (itemKey, value) => {
    setTableRows((prev) => prev.map((row) => {
      if (!row.isEditable) return row;
      return {
        ...row,
        items: row.items.map((item) =>
          item.itemKey === itemKey ? { ...item, dailyNotes: value } : item
        ),
      };
    }));
  };

  const applySummariesToTable = (nextSummaries, removedWorkDateIsos) => {
    const removed = removedWorkDateIsos instanceof Set
      ? removedWorkDateIsos
      : new Set(removedWorkDateIsos ? [removedWorkDateIsos] : []);
    const cache = tableCacheRef.current;
    tableCacheRef.current = {
      ...cache,
      dateSummaries: nextSummaries,
    };

    const editable = tableRows.find(
      (row) => row.isEditable && !removed.has(row.workDateIso)
    );
    let mapped = mapSummariesToTableRows(
      nextSummaries,
      cache.prodRows,
      workDate,
      canEditProductionForWorkDate
    );

    if (removed.has(workDate) && canEditProductionForWorkDate) {
      const installedToDateByItem = latestCumulativeOnOrBefore(nextSummaries, workDate);
      mapped.push({
        workDateIso: workDate,
        date: formatIsoDateToTable(workDate),
        isEditable: true,
        isSaved: false,
        items: buildItemsFromLogContext(cache.prodRows, new Map(), installedToDateByItem),
      });
    } else if (editable && !nextSummaries.some((summary) => summary.workDate === editable.workDateIso)) {
      const installedToDateByItem = latestCumulativeOnOrBefore(nextSummaries, editable.workDateIso);
      const freshItems = buildItemsFromLogContext(cache.prodRows, new Map(), installedToDateByItem);
      mapped.push({
        ...editable,
        isSaved: false,
        items: freshItems.map((item) => {
          const edited = editable.items.find((entry) => entry.itemKey === item.itemKey);
          if (!edited) return item;
          return updateInstalledItem(
            { ...item, dailyNotes: edited.dailyNotes ?? "" },
            edited.new
          );
        }),
      });
    } else if (editable) {
      mapped = mapped.map((row) => {
        if (row.workDateIso !== editable.workDateIso) return row;
        return {
          ...row,
          items: row.items.map((item) => {
            const edited = editable.items.find((entry) => entry.itemKey === item.itemKey);
            if (!edited) return item;
            return updateInstalledItem(
              { ...item, dailyNotes: edited.dailyNotes ?? item.dailyNotes },
              edited.new
            );
          }),
        };
      });
    }

    mapped.sort((a, b) => b.workDateIso.localeCompare(a.workDateIso));
    setTableRows(mapped);
  };

  const handleOpenDeleteConfirm = (row) => {
    if (deleting || saving) return;
    setDeleteConfirm({ isOpen: true, rows: [row] });
  };

  const handleEnterSelectDeleteMode = () => {
    if (deleting || saving || tableRows.length === 0) return;
    setSelectedWorkDates([]);
    setIsSelectDeleteMode(true);
  };

  const handleExitSelectDeleteMode = () => {
    if (deleting) return;
    setIsSelectDeleteMode(false);
    setSelectedWorkDates([]);
  };

  const handleToggleRowSelected = (workDateIso, checked) => {
    const iso = String(workDateIso);
    setSelectedWorkDates((prev) => {
      if (checked) return prev.includes(iso) ? prev : [...prev, iso];
      return prev.filter((value) => value !== iso);
    });
  };

  const handleToggleAllRowsSelected = (checked) => {
    setSelectedWorkDates(checked ? tableRows.map((row) => row.workDateIso) : []);
  };

  const handleOpenBulkDeleteConfirm = () => {
    if (deleting || selectedWorkDateSet.size === 0) return;
    const toDelete = tableRows.filter((row) => selectedWorkDateSet.has(row.workDateIso));
    if (toDelete.length === 0) return;
    setDeleteConfirm({ isOpen: true, rows: toDelete });
  };

  const handleCloseDeleteConfirm = () => {
    if (deleting) return;
    setDeleteConfirm({ isOpen: false, rows: [] });
  };

  const handleConfirmDeleteRow = async () => {
    const targets = deleteConfirm.rows || [];
    if (targets.length === 0 || deleting) return;
    const removed = new Set(targets.map((row) => row.workDateIso));

    workDateRequestSeqRef.current += 1;
    laborAbortRef.current?.abort();
    if (workDateDebounceRef.current) {
      window.clearTimeout(workDateDebounceRef.current);
      workDateDebounceRef.current = null;
    }

    const cache = tableCacheRef.current;
    const resolvedScopeId = cache.resolvedScopeId || scopeId;
    const savedDates = (cache.dateSummaries || [])
      .map((summary) => summary.workDate)
      .filter((iso) => removed.has(iso));

    setDeleting(true);
    setSaveMessage(null);
    try {
      let nextSummaries = recalculateSummariesWithoutDates(cache.dateSummaries, removed);
      let productionTracking = null;

      if (savedDates.length > 0) {
        if (!projectNo || !resolvedScopeId) {
          throw new Error("Project and activity are required to delete these dates.");
        }
        const base = process.env.REACT_APP_BACKEND_BASEURL;
        const res = await axios.post(
          `${base}/api/miter/production-performance/${encodeURIComponent(projectNo)}/production-logs/delete`,
          {
            scopeId: resolvedScopeId,
            scopeName: division,
            workDates: savedDates,
          },
          await getAuthConfig()
        );
        if (res.data?.status !== "success") {
          throw new Error(res.data?.message || "Failed to delete production logs.");
        }
        if (Array.isArray(res.data?.data?.dates)) {
          nextSummaries = res.data.data.dates;
        }
        const stillPresent = nextSummaries.some((summary) => removed.has(summary.workDate));
        if (stillPresent) {
          throw new Error("Those production logs could not be deleted.");
        }
        productionTracking = res.data?.data?.productionTracking ?? null;
        workDateRequestSeqRef.current += 1;
        const productionUpdate = {
          projectCode: projectNo,
          scopeId: resolvedScopeId,
          scopeName: division,
          productionTracking,
        };
        onProductionSaved?.(productionUpdate);
        window.dispatchEvent(new CustomEvent("nettles-production-saved", { detail: productionUpdate }));
      }

      applySummariesToTable(nextSummaries, removed);
      setDeleteConfirm({ isOpen: false, rows: [] });
      if (targets.length > 1 || nextSummaries.length === 0) {
        setIsSelectDeleteMode(false);
        setSelectedWorkDates([]);
      } else {
        setSelectedWorkDates((prev) => prev.filter((iso) => !removed.has(iso)));
      }
      setSaveMessage({
        type: "success",
        text: targets.length === 1
          ? `Production log for ${targets[0].date} deleted.`
          : `${targets.length} production logs deleted.`,
      });
    } catch (err) {
      setDeleteConfirm({ isOpen: false, rows: [] });
      setSaveMessage({
        type: "error",
        validationBlocks: [{
          messageVariant: "generic",
          title: "Cannot delete",
          subtitle: err.response?.data?.message || err.message || "Failed to delete production logs.",
        }],
      });
    } finally {
      setDeleting(false);
    }
  };

  const continueToInventory = () => {
    onLogInventory?.({
      projectNo,
      projectName,
      workDate,
      division,
      scopeId,
      projectActivities,
    });
  };

  const handleSubmit = async () => {
    setSaveMessage(null);

    // Nothing to save for the selected work date (empty table, or no timesheet
    // so there is no editable row). Continue to inventory instead of blocking.
    const hasEditableProductionRow = tableRows.some((row) => row.isEditable);
    if (
      !loading &&
      !error &&
      (tableRows.length === 0 ||
        !canEditProductionForWorkDate ||
        !hasEditableProductionRow)
    ) {
      if (!projectNo || !scopeId || !workDate) {
        setSaveMessage({
          type: "error",
          validationBlocks: [{
            messageVariant: "generic",
            title: "Cannot save — required fields missing",
            subtitle: "Project, date, and scope are required.",
          }],
        });
        return;
      }
      continueToInventory();
      return;
    }

    if (!runSaveValidations()) return;

    const editableRow = tableRows.find((r) => r.isEditable);
    if (!editableRow) return;

    try {
      setSaving(true);
      setSaveMessage(null);
      const base = process.env.REACT_APP_BACKEND_BASEURL;
      const entries = editableRow.items.map((item) => ({
        itemKey: item.itemKey,
        itemName: item.name,
        newlyInstalled: parseNumber(item.new),
        dailyNotes: item.dailyNotes || "",
      }));

      const saveResponse = await axios.post(
        `${base}/api/miter/production-performance/${encodeURIComponent(projectNo)}/production-logs/save`,
        {
          scopeId,
          scopeName: division,
          workDate,
          entries,
        },
        await getAuthConfig()
      );
      const productionUpdate = {
        projectCode: projectNo,
        scopeId,
        scopeName: division,
        productionTracking: saveResponse.data?.data?.productionTracking || null,
      };
      onProductionSaved?.(productionUpdate);
      window.dispatchEvent(new CustomEvent("nettles-production-saved", { detail: productionUpdate }));
      continueToInventory();
    } catch (err) {
      setSaveMessage({
        type: "error",
        validationBlocks: [{
          messageVariant: "generic",
          title: "Cannot save",
          subtitle: err.response?.data?.message || err.message || "Failed to save production tracking.",
        }],
      });
    } finally {
      setSaving(false);
    }
  };

  const handleOpenUpdateMultiple = () => {
    if (loading || saving || deleting || !productionReady) return;
    const prodRows = tableCacheRef.current.prodRows || [];
    const items = prodRows.map((row, idx) => ({
      itemKey: buildItemKey(row, idx),
      name: row.description || row.activity || "Production quantity",
    }));
    const existingByDate = {};
    tableRows.forEach((row) => {
      if (!row.workDateIso) return;
      const savedItems = {};
      (row.items || []).forEach((item) => {
        savedItems[item.itemKey] = {
          newlyInstalled: item.new,
          dailyNotes: item.dailyNotes || "",
        };
      });
      existingByDate[row.workDateIso] = savedItems;
    });
    const ensureDates = [];
    if (canEditProductionForWorkDate && /^\d{4}-\d{2}-\d{2}$/.test(workDate)) {
      ensureDates.push(workDate);
    }
    const hint = crewLaborHintRef.current;
    if (hint?.hasCrewProjectLabor && /^\d{4}-\d{2}-\d{2}$/.test(hint.workDate || "")) {
      ensureDates.push(hint.workDate);
    }
    setUpdateMultipleSession({ items, existingByDate, ensureDates });
    setIsUpdateMultipleOpen(true);
  };

  const applyBulkSavedSummaries = (nextSummaries, savedWorkDates) => {
    const saved = savedWorkDates instanceof Set ? savedWorkDates : new Set(savedWorkDates || []);
    const cache = tableCacheRef.current;
    tableCacheRef.current = {
      ...cache,
      dateSummaries: nextSummaries,
    };
    const draft = tableRows.find((row) => row.isEditable && !saved.has(row.workDateIso));
    let mapped = rowsForWorkDate(
      nextSummaries,
      cache.prodRows,
      workDate,
      canEditProductionForWorkDate
    );
    if (draft) {
      const alreadyListed = mapped.some((row) => row.workDateIso === draft.workDateIso);
      if (!alreadyListed) {
        mapped.push({ ...draft, isEditable: true, isSaved: false });
      } else {
        mapped = mapped.map((row) => {
          if (row.workDateIso !== draft.workDateIso) return row;
          return {
            ...row,
            isEditable: true,
            items: row.items.map((item) => {
              const edited = draft.items.find((entry) => entry.itemKey === item.itemKey);
              if (!edited) return item;
              return updateInstalledItem(
                { ...item, dailyNotes: edited.dailyNotes ?? item.dailyNotes },
                edited.new
              );
            }),
          };
        });
      }
      mapped.sort((a, b) => b.workDateIso.localeCompare(a.workDateIso));
    }
    setTableRows(mapped);
  };

  const handleUpdateMultipleSave = async ({ updates }) => {
    const targets = Array.isArray(updates) ? updates : [];
    if (targets.length === 0) {
      throw new Error("Select at least one date.");
    }
    const cache = tableCacheRef.current;
    const resolvedScopeId = cache.resolvedScopeId || scopeId;
    if (!projectNo || !resolvedScopeId) {
      throw new Error("Project and activity are required to save production.");
    }

    workDateRequestSeqRef.current += 1;
    laborAbortRef.current?.abort();
    if (workDateDebounceRef.current) {
      window.clearTimeout(workDateDebounceRef.current);
      workDateDebounceRef.current = null;
    }
    setLaborCheckPending(false);
    setWorkDateLoading(false);

    const base = process.env.REACT_APP_BACKEND_BASEURL;
    const savedWorkDates = [];
    let productionTracking = null;
    try {
      for (const update of targets) {
        const res = await axios.post(
          `${base}/api/miter/production-performance/${encodeURIComponent(projectNo)}/production-logs/save`,
          {
            scopeId: resolvedScopeId,
            scopeName: division,
            workDate: update.workDate,
            entries: update.entries,
          },
          await getAuthConfig()
        );
        if (res.data?.status !== "success") {
          throw new Error(res.data?.message || "Failed to save production tracking.");
        }
        productionTracking = res.data?.data?.productionTracking ?? productionTracking;
        savedWorkDates.push(update.workDate);
      }
    } catch (err) {
      if (savedWorkDates.length > 0) {
        try {
          const params = new URLSearchParams();
          if (division) params.set("scope", division);
          const qs = params.toString();
          const listRes = await axiosGetWithRateLimitRetry(
            `${base}/api/miter/production-performance/${encodeURIComponent(projectNo)}/production-logs/${encodeURIComponent(resolvedScopeId)}${qs ? `?${qs}` : ""}`,
            await getAuthConfig()
          );
          const summaries = Array.isArray(listRes.data?.data?.dates)
            ? listRes.data.data.dates
            : tableCacheRef.current.dateSummaries;
          applyBulkSavedSummaries(summaries, savedWorkDates);
        } catch {
          // The dates that saved stay on the server. The table refreshes on the next load.
        }
      }
      throw new Error(
        err.response?.data?.message || err.message || "Failed to save production tracking."
      );
    }

    const params = new URLSearchParams();
    if (division) params.set("scope", division);
    const qs = params.toString();
    const listRes = await axiosGetWithRateLimitRetry(
      `${base}/api/miter/production-performance/${encodeURIComponent(projectNo)}/production-logs/${encodeURIComponent(resolvedScopeId)}${qs ? `?${qs}` : ""}`,
      await getAuthConfig()
    );
    const summaries = Array.isArray(listRes.data?.data?.dates)
      ? listRes.data.data.dates
      : tableCacheRef.current.dateSummaries;
    applyBulkSavedSummaries(summaries, savedWorkDates);

    const productionUpdate = {
      projectCode: projectNo,
      scopeId: resolvedScopeId,
      scopeName: division,
      productionTracking,
    };
    onProductionSaved?.(productionUpdate);
    window.dispatchEvent(new CustomEvent("nettles-production-saved", { detail: productionUpdate }));
    keepSuccessRef.current = true;
    setSaveMessage({
      type: "success",
      text: savedWorkDates.length === 1
        ? "Updated 1 Production day."
        : `Updated ${savedWorkDates.length} Production days.`,
    });
  };

  const selectedWorkDateSet = useMemo(() => new Set(selectedWorkDates), [selectedWorkDates]);
  const savedDateCount = useMemo(() => {
    const dates = new Set();
    tableRows.forEach((row) => {
      if (row.isSaved && row.workDateIso) dates.add(row.workDateIso);
    });
    return dates.size;
  }, [tableRows]);
  const selectedCount = tableRows.reduce(
    (count, row) => count + (selectedWorkDateSet.has(row.workDateIso) ? 1 : 0),
    0
  );
  const deleteTargets = deleteConfirm.rows || [];
  const isBulkDelete = deleteTargets.length > 1;
  const deleteTargetDate = deleteTargets[0]?.date;

  const handleFlowStep = (step) => {
    onFlowNavigate?.(step, {
      projectNo,
      projectName,
      workDate,
      division,
      scopeId,
      projectActivities,
    });
  };
  const flowNavDisabled =
    loading || saving || deleting || workDateLoading || laborCheckPending;

  return (
    <>
    <div className="flex min-h-0 h-full flex-1 flex-col overflow-hidden">
      <Header
        onClose={onClose}
        onFlowStep={onFlowNavigate ? handleFlowStep : undefined}
        navDisabled={flowNavDisabled}
      />
      <SaveValidationBanners
        validationBlocks={saveMessage?.validationBlocks}
        onDismiss={dismissValidationBlock}
      />
      <div className="nettles-flow-sheet-scroll flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden max-lg:landscape:overflow-y-auto max-lg:landscape:[scrollbar-gutter:stable] max-lg:landscape:[-webkit-overflow-scrolling:touch]">
      <div className="nettles-flow-sheet-body flex-1 min-h-0 min-w-0 overflow-y-auto overflow-x-clip max-lg:landscape:flex-none max-lg:landscape:overflow-y-visible">
        <DailyLogProjectFields
          projectNo={projectNo}
          projectName={projectName}
          workDate={workDate}
          onWorkDateChange={setWorkDate}
          activity={division}
        />
        {!loading && !error && (
          <div className="px-4 md:px-6 pt-3 flex flex-wrap items-center justify-end gap-3">
            <div className="mr-auto flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1">
              <p className="text-xs text-gray-500">
                <span className="font-medium text-[#1E5A8A]">
                  Total Production logged days: {savedDateCount}
                </span>
              </p>
              {(laborCheckPending || workDateLoading) ? (
                <>
                  <span className="text-xs text-gray-300" aria-hidden>·</span>
                  <p className="text-sm text-gray-500" aria-live="polite">
                    Checking timesheet for {formatIsoDateToMdy(workDate)}…
                  </p>
                </>
              ) : !canEditProductionForWorkDate ? (
                <>
                  <span className="text-xs text-gray-300" aria-hidden>·</span>
                  <p className="text-sm text-gray-500">
                    No timesheet data for {formatIsoDateToMdy(workDate)} in Miter. Change Work Date to log production.
                  </p>
                </>
              ) : null}
            </div>
            {isSelectDeleteMode ? (
              <div className="flex items-center gap-3 shrink-0">
                <span className="text-sm text-gray-600 whitespace-nowrap">
                  {selectedCount > 0 ? `${selectedCount} selected` : "Select dates to delete"}
                </span>
                <button
                  type="button"
                  onClick={handleExitSelectDeleteMode}
                  disabled={deleting}
                  className="h-9 px-4 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleOpenBulkDeleteConfirm}
                  disabled={deleting || selectedCount === 0}
                  className="h-9 px-4 rounded-md border border-red-200 bg-white text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Delete selected
                </button>
              </div>
            ) : (
              <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-4 shrink-0">
                <button
                  type="button"
                  onClick={handleEnterSelectDeleteMode}
                  disabled={deleting || saving || tableRows.length === 0}
                  className="h-9 px-4 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Delete Multiple
                </button>
                <button
                  type="button"
                  onClick={handleOpenUpdateMultiple}
                  disabled={loading || saving || deleting || !productionReady}
                  className="flex items-center gap-2 text-[15px] sm:text-[16px] font-normal text-[#AD2226] underline font-poppins leading-none focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed disabled:no-underline"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    className="w-6 h-6"
                    fill="none"
                    aria-hidden
                  >
                    <path
                      d="M10 6C10 7.10457 10.8954 8 12 8C13.1046 8 14 7.10457 14 6C14 4.89543 13.1046 4 12 4C10.8954 4 10 4.89543 10 6Z"
                      fill="#AD2226"
                    />
                    <path
                      d="M10 12C10 13.1046 10.8954 14 12 14C13.1046 14 14 13.1046 14 12C14 10.8954 13.1046 10 12 10C10.8954 10 10 10.8954 10 12Z"
                      fill="#AD2226"
                    />
                    <path
                      d="M12 20C10.8954 20 10 19.1046 10 18C10 16.8954 10.8954 16 12 16C13.1046 16 14 16.8954 14 18C14 19.1046 13.1046 20 12 20Z"
                      fill="#AD2226"
                    />
                  </svg>
                  <span>Update Multiple</span>
                </button>
              </div>
            )}
          </div>
        )}
        <ProductionTable
          loading={loading}
          error={error}
          tableRows={tableRows}
          onChangeInstalled={handleChangeInstalled}
          onBlurInstalled={handleBlurInstalled}
          onChangeNotes={handleChangeNotes}
          onDeleteRow={handleOpenDeleteConfirm}
          deleteDisabled={deleting || saving}
          compactTopSpacing={!loading && !error}
          isSelectDeleteMode={isSelectDeleteMode}
          selectedWorkDates={selectedWorkDateSet}
          onToggleRowSelected={handleToggleRowSelected}
          onToggleAllRowsSelected={handleToggleAllRowsSelected}
          hideEmptyState={workDateLoading || laborCheckPending}
        />
      </div>

      <div className="shrink-0 border-t border-gray-200 bg-white px-4 sm:px-6 py-3 sm:py-4 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
        <div className="flex flex-col items-center gap-2">
          {saveMessage?.type === "success" && saveMessage.text && (
            <p className="text-center text-sm px-1 text-green-600 max-w-[42rem]">
              {saveMessage.text}
            </p>
          )}
          <div className="flex w-full flex-nowrap sm:flex-wrap items-center justify-center gap-3">
            {onFlowNavigate && (
              <DailyLogBackButton
                currentStep="production"
                onStepChange={handleFlowStep}
                disabled={flowNavDisabled}
              />
            )}
            <SubmitButton
              onSubmit={handleSubmit}
              disabled={loading || saving || deleting || workDateLoading || laborCheckPending}
              saving={saving}
            />
          </div>
        </div>
      </div>
      </div>
    </div>
    <ConfirmDialog
      isOpen={deleteConfirm.isOpen}
      title={isBulkDelete ? "Delete Production Logs" : "Delete Production Log"}
      message={
        isBulkDelete
          ? `Are you sure you want to delete ${deleteTargets.length} production logs?`
          : `Are you sure you want to delete the production log for ${deleteTargetDate || "this date"}?`
      }
      confirmLabel={isBulkDelete ? "Delete selected" : "Delete"}
      cancelLabel="Cancel"
      onConfirm={handleConfirmDeleteRow}
      onClose={handleCloseDeleteConfirm}
      isLoading={deleting}
      isDestructive
      overlayClassName="z-[10280]"
    />
    <ProductionTrackingUpdateMultipleModal
      isOpen={isUpdateMultipleOpen}
      onClose={() => {
        if (saving) return;
        setIsUpdateMultipleOpen(false);
      }}
      projectNo={projectNo}
      scopeId={tableCacheRef.current.resolvedScopeId || scopeId}
      scopeName={division}
      templateItems={updateMultipleSession?.items || []}
      existingByDate={updateMultipleSession?.existingByDate || {}}
      ensureDates={updateMultipleSession?.ensureDates || []}
      getAuthConfig={getAuthConfig}
      onSave={handleUpdateMultipleSave}
    />
    </>
  );
};

/* ================= MODAL ================= */

const ProductionLoggedModal = ({
  isOpen,
  onClose,
  initialContext,
  onLogInventory,
  onProductionSaved,
  onFlowNavigate,
  preserveWhenHidden = false,
}) => {
  useFullscreenSheetModal(isOpen);

  if (!isOpen && !preserveWhenHidden) return null;

  return createPortal(
    <div
      aria-hidden={!isOpen}
      className={`fixed inset-0 z-[10160] items-stretch justify-center bg-black/50 ${
        isOpen ? 'flex' : 'hidden'
      }`}
    >
      <div className="nettles-flow-sheet-panel absolute inset-2 lg:inset-6 bg-white rounded-xl shadow-2xl flex flex-col overflow-x-clip overflow-y-hidden max-lg:top-[max(0.5rem,env(safe-area-inset-top))] max-lg:bottom-[max(0.5rem,env(safe-area-inset-bottom))] max-lg:left-[max(0.5rem,env(safe-area-inset-left))] max-lg:right-[max(0.5rem,env(safe-area-inset-right))]">
        <ProductionTrackingContent
          onClose={onClose}
          initialContext={initialContext}
          onLogInventory={onLogInventory}
          onProductionSaved={onProductionSaved}
          onFlowNavigate={onFlowNavigate}
          isActive={isOpen}
        />
      </div>
    </div>,
    document.body
  );
};

export default ProductionLoggedModal;
